import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { I18nextProvider } from "react-i18next";
import { ApiError } from "@/lib/api";
import { i18n } from "@/lib/i18n";
import { installScreenTransitions, viewTransitionTypesFor } from "@/lib/screenTransitions";
import { routeTree } from "./routeTree.gen";

// The router factory TanStack Start calls on both the server (once per
// prerendered page, ADR-0053 §1) and the client (once, at hydration). It
// replaces the imperative bootstrap that used to live in `main.tsx`: the router
// is created here so a single definition drives prerender, hydration, and dev.
//
// App-wide providers hang off the router's `Wrap` (providers only — no
// DOM-rendering, per the router contract) so QueryClient + i18n context wrap the
// whole tree identically under SSR and on the client. The QueryClient is created
// per `getRouter` call, so a prerender pass never shares cache across pages.
export function getRouter() {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        throwOnError: (error) => error instanceof ApiError && error.status >= 500,
      },
    },
  });

  const router = createRouter({
    routeTree,
    scrollRestoration: true,
    // Screen transitions (ADR-0052): wrap every route-to-route navigation in a
    // browser view transition. `types` decides the character purely from the
    // pathnames (see `viewTransitionTypesFor`). All the animation itself is
    // styled on `::view-transition-*(root)` in index.css.
    defaultViewTransition: {
      types: ({ fromLocation, toLocation }) =>
        viewTransitionTypesFor(fromLocation?.pathname, toLocation.pathname),
    },
    Wrap: ({ children }: { children: ReactNode }) => (
      <I18nextProvider i18n={i18n}>
        <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
      </I18nextProvider>
    ),
  });

  // Expose the navigation direction on <html data-nav-direction> before each
  // transition starts. Guarded against the server (no-op without `document`), so
  // it is safe to call inside the isomorphic factory (see `screenTransitions`).
  installScreenTransitions(router);

  return router;
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof getRouter>;
  }
}
